import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Lottie from 'lottie-react';

import Sandbox from './Sandbox';
import SideBarAnimation from '../../components/common/SideBarAnimation';

import { Routes } from '../../utils/constants/routes';
import { ROLES_MAPPING } from '../../utils/constants';

import { useAuthStore } from '../../stores/authStore';
import { useCommonStore } from '../../stores/commonStore';

import { useInterval } from '../../hooks/useInterval';

import { getRequestStatus } from '../../utils/api/requestApi';
import { calculateTriggerPipelinePercentage } from '../../utils/commonUtils';
import { transformPipelineResponseToIntermediateFSNModel } from '../../utils/transformers';

import waveLoaderAnimation from '../../static/wave-loader.json';

import { VisualQCStatus } from '../../utils/enums';
import ToastMessageDialogue from '../../components/common/ToastMessageDialogue';
import { FSNModel } from '../../types/common';

const DELAY = 20000;

const GetModelStatus = ({ currentRequestId, isFailed, percentage }) => {
    if (!currentRequestId && !isFailed) {
        return <h1>Please Upload Models First</h1>;
    } else if (currentRequestId && !isFailed) {
        return (
            <div className="flex flex-col">
                <div className="flex justify-center items-center w-full h-auto">
                    <Lottie animationData={waveLoaderAnimation} loop={true} autoPlay={true} />
                </div>
                <h1 className="w-full text-center">Running the pipeline, Please keep the tab open !</h1>
            </div>
        );
    } else if (isFailed) {
        return <h1>Your model is failed. please check it and try again</h1>;
    } else {
        return <h1>Something went wrong</h1>;
    }
};

const PreviewFSNModels = () => {
    const [isPendingRequest, setIsPendingRequest] = useState<boolean>(false);

    const [isFailed, setIsFailed] = useState<boolean>(false);
    const [triggerPipelineResponseLength, setTriggerPipelineResponseLength] = useState(0);

    const activeRole = useAuthStore((state) => state.activeRole);
    const activePartner = useAuthStore((state) => state.activePartner);

    const sideBarOpen: boolean = useCommonStore((state) => state.sideBarOpen);
    const currentRequestId = useCommonStore((state) => state.currentRequestId);
    const threeDLiveFsns = useCommonStore((state) => state.threeDLiveFsns);
    const modelUploadApiResponse = useCommonStore((state) => state.modelUploadApiResponse);
    const setThreeDLiveFsns = useCommonStore((state) => state.setThreeDLiveFsns);
    const setToastMessage = useCommonStore((state) => state.setToastMessage);
    const setActiveFSN = useCommonStore((state) => state.setActiveFSN);

    const history = useHistory();

    const triggerPipelinePercentage = calculateTriggerPipelinePercentage(triggerPipelineResponseLength, modelUploadApiResponse);
    const modelStatus = <GetModelStatus currentRequestId={currentRequestId} isFailed={isFailed} percentage={triggerPipelinePercentage} />;
    const get3DFeaturePipelineResponse = async () => {
        try {
            const requestParams = {
                requestId: currentRequestId
            };

            // console.log('readimg request' + currentRequestId);

            const response = await getRequestStatus<any>(requestParams);

            setTriggerPipelineResponseLength(response.data.length);

            // console.log(response, 'pipe status');

            if (response && response.data.length >= 1) {
                const data = [...modelUploadApiResponse, ...response.data];

                const result = transformPipelineResponseToIntermediateFSNModel(data, activePartner);

                // console.log(result, 'red status');

                const somePendingRequest = result.some((item) => item.status === 'PENDING');

                // console.log(somePendingRequest, 'req pending status');

                if (somePendingRequest) {
                    return;
                }

                // console.log('request not pending');

                setIsPendingRequest(false);

                // const failedFSNs =
                const someFailedRequest = result.some((item) => item.status === 'FAILED');

                const newThreeDLiveFsns: FSNModel[] = result.map((item) => ({
                    fsn: item.uniqueId,
                    url: item.url ?? '',
                    error: item.error,
                    status: item.status === 'COMPLETED',
                    fitFor3DInPP: item.fitFor3DInPP || false,
                    visualQCStatus: VisualQCStatus.PENDING
                }));
                setThreeDLiveFsns(newThreeDLiveFsns);

                if (someFailedRequest) {
                    setToastMessage({ type: 'warn', message: 'Warning', description: 'One or more products failed to process. Download Report for error details' });
                } else {
                    setToastMessage({ type: 'success', message: 'Success', description: 'Pipeline processed assets successfully' });
                }

                const firstCompletedFSNIndex = newThreeDLiveFsns.findIndex((item) => item.status === true);

                if (firstCompletedFSNIndex !== -1) {
                    setActiveFSN(newThreeDLiveFsns[firstCompletedFSNIndex].fsn);
                }
            }
        } catch (e) {
            setToastMessage({ type: 'error', message: 'Something went wrong while getting the request status' });
            // setIsPendingRequest(false);
            console.log('Error while getting the request status:', e);
        }
    };

    useInterval(get3DFeaturePipelineResponse, isPendingRequest ? DELAY : null);

    const handleLogout = () => {
        localStorage.clear();
        history.push(Routes.LOGIN);
    };

    useEffect(() => {
        if (currentRequestId) {
            setIsPendingRequest(true);
        }
    }, []);

    // console.log(activeRole);

    return (
        <main className="flex w-full h-full">
            <ToastMessageDialogue />
            <SideBarAnimation open={sideBarOpen} type={ROLES_MAPPING[activeRole]} onClick={handleLogout} />
            {threeDLiveFsns?.length ? <Sandbox /> : <div className="flex justify-center items-center h-full w-full">{modelStatus}</div>}
        </main>
    );
};

export default React.memo(PreviewFSNModels);
